import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {useAuth} from "../context/AuthContext";
import {getFromApi, putToApi} from "../api/auth";

const STATS = [
    {key: "notes",       label: "Notes",        icon: "bi-journal-text",  color: "#6366f1", bg: "#eef2ff", to: "/notes"},
    {key: "bookmarks",   label: "Bookmarks",    icon: "bi-bookmark",      color: "#0ea5e9", bg: "#e0f2fe", to: "/bookmarks"},
    {key: "projects",    label: "Projects",     icon: "bi-kanban",        color: "#f59e0b", bg: "#fef3c7", to: "/project-log"},
    {key: "pendingTodos",label: "Pending tasks", icon: "bi-check2-square", color: "#10b981", bg: "#d1fae5", to: "/todo"},
];

function StatCard({stat, value, loading}) {
    return (
        <Link to={stat.to} className="stat-card">
            <div className="stat-card-icon" style={{background: stat.bg, color: stat.color}}>
                <i className={`bi ${stat.icon}`} />
            </div>
            <div className="flex-grow-1">
                <div className="fw-semibold">{stat.label}</div>
                <div className="small text-muted">{loading ? "—" : value}</div>
            </div>
            <i className="bi bi-chevron-right text-muted small" />
        </Link>
    );
}

function PendingTodos({items, loading, onComplete}) {
    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="fw-semibold mb-0">
                    <i className="bi bi-check2-square me-2 text-success" />Pending tasks
                </h6>
                <Link to="/todo" className="small text-primary text-decoration-none">View all</Link>
            </div>

            {loading ? (
                <div className="text-muted small py-2">Loading...</div>
            ) : !items.length ? (
                <div className="empty-state py-4">
                    <i className="bi bi-check2-all" style={{fontSize: "1.75rem"}} />
                    <div className="small mt-1">All caught up!</div>
                </div>
            ) : (
                <div>
                    {items.map(item => (
                        <div key={item._id} className="d-flex align-items-center gap-3 py-2 border-bottom">
                            <button
                                className="btn p-0 border-0 flex-shrink-0"
                                onClick={() => onComplete(item)}
                                title="Mark done"
                            >
                                <i className="bi bi-circle text-muted" style={{fontSize: "1.1rem"}} />
                            </button>
                            <div className="flex-grow-1 min-w-0">
                                <div className="text-truncate small fw-medium">{item.name}</div>
                                <div className="text-muted" style={{fontSize: "0.75rem"}}>{item.groupName}</div>
                            </div>
                            {item.due && (
                                <span className="badge text-bg-light text-muted flex-shrink-0" style={{fontSize: "0.7rem"}}>
                                    {item.due}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function RecentActivity({items, loading}) {
    const icons = {
        note: {icon: "bi-journal-text", color: "#6366f1", bg: "#eef2ff"},
        log:  {icon: "bi-kanban",       color: "#f59e0b", bg: "#fef3c7"},
    };
    const formatDate = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        const diff = Date.now() - d.getTime();
        const days = Math.floor(diff / 86400000);
        if (days === 0) return "Today";
        if (days === 1) return "Yesterday";
        return d.toLocaleDateString(undefined, {day: "numeric", month: "short"});
    };

    return (
        <div>
            <h6 className="fw-semibold mb-3">
                <i className="bi bi-clock-history me-2 text-muted" />Recent activity
            </h6>

            {loading ? (
                <div className="text-muted small py-2">Loading...</div>
            ) : !items.length ? (
                <p className="text-muted small mb-0">No recent activity yet.</p>
            ) : (
                <div>
                    {items.map(item => {
                        const style = icons[item.type] || icons.note;
                        return (
                            <Link key={item._id} to={item.url} className="d-flex align-items-center gap-3 py-2 border-bottom text-decoration-none text-dark">
                                <div className="stat-card-icon flex-shrink-0" style={{width: 34, height: 34, background: style.bg, color: style.color, borderRadius: 8, fontSize: "0.9rem"}}>
                                    <i className={`bi ${style.icon}`} />
                                </div>
                                <div className="flex-grow-1 min-w-0">
                                    <div className="text-truncate small fw-medium">{item.title}</div>
                                    <div className="text-muted" style={{fontSize: "0.75rem"}}>{item.parentName}</div>
                                </div>
                                <span className="text-muted flex-shrink-0" style={{fontSize: "0.75rem"}}>{formatDate(item.date)}</span>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function Dashboard() {
    const {user} = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const res = await getFromApi("api/dashboard");
            setData(res.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleComplete = async (item) => {
        await putToApi(`api/todo/${item.parent_id}/items/${item._id}`, {});
        load();
    };

    const hour = new Date().getHours();
    const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
    const name = user?.email?.split("@")[0];
    const today = new Date().toLocaleDateString(undefined, {weekday: "long", day: "numeric", month: "long"});

    return (
        <div>
            {/* Header */}
            <div className="d-flex align-items-end justify-content-between mb-4">
                <div>
                    <h4 className="mb-0">{greeting}{name ? `, ${name}` : ""}</h4>
                    <p className="text-muted small mb-0">{today}</p>
                </div>
            </div>

            {/* Stats row */}
            <div className="row g-3 mb-4">
                {STATS.map(stat => (
                    <div key={stat.key} className="col-sm-6 col-xl-3">
                        <StatCard stat={stat} value={data?.stats?.[stat.key] ?? 0} loading={loading} />
                    </div>
                ))}
            </div>

            {/* Pending todos + Recent activity */}
            <div className="row g-3">
                <div className="col-md-6">
                    <div className="panel h-100">
                        <PendingTodos
                            items={data?.pending || []}
                            loading={loading}
                            onComplete={handleComplete}
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="panel h-100">
                        <RecentActivity items={data?.recentActivity || []} loading={loading} />
                    </div>
                </div>
            </div>
        </div>
    );
}
